// src/accounts/pages/AccountBirthdays.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

function toSlug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeItem(raw) {
  const b = raw || {};

  // Fix "undefined" name + date
  const name = (b.name || b.label || "").trim() || "Saved birthday";
  const birthdayRaw = b.birthday || b.date || b.birthdate || "";
  const birthday = String(birthdayRaw).slice(0, 10); // yyyy-mm-dd

  // Try to resolve element/animal/slug from whatever exists
  let element = (b.element || "").toString().trim();
  let animal = (b.animal || "").toString().trim();
  let slug = (b.slug || "").toString().trim();

  // If slug exists like "wood-snake", parse it
  if (!element && !animal && slug && slug.includes("-")) {
    const parts = slug.split("-");
    if (parts.length >= 2) {
      element = parts[0];
      animal = parts.slice(1).join("-");
    }
  }

  // If we have element+animal but no slug, build it
  if (!slug && element && animal) {
    slug = `${toSlug(element)}-${toSlug(animal)}`;
  }

  return {
    ...b,
    id: b.id ?? `${name}-${birthday}-${slug}`,
    name,
    birthday,
    element,
    animal,
    slug,
  };
}

export default function AccountBirthdays({ items = [], onDelete }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const rows = useMemo(() => (items || []).map(normalizeItem), [items]);

  function openModal(item) {
    setSelected(item);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setSelected(null);
  }

  function goToDetails(item) {
    // Prefer full element details page if we can
    if (item?.slug) return nav(`/five-elements-zodiac/${item.slug}`);

    // fallback to animal page
    if (item?.animal) return nav(`/chinese-zodiac/${toSlug(item.animal)}`);

    // last fallback
    return nav("/");
  }

  return (
    <section>
      <h2>Saved birthdays</h2>

      {rows.length === 0 && <p>No saved birthdays yet.</p>}

      {rows.map((b) => (
        <div key={b.id} style={row}>
          <div style={rowInner}>
            <div>
              <div style={{ fontWeight: 900 }}>{b.name}</div>
              <div style={{ opacity: 0.75 }}>{b.birthday || "—"}</div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                type="button"
                title="View"
                onClick={() => openModal(b)}
                style={iconBtn}
              >
                🔍
              </button>

              <button
                type="button"
                title="Delete"
                onClick={() => onDelete?.(b.id)}
                style={iconBtnGhost}
              >
                🗑️
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Modal */}
      {open && (
        <div style={overlay} onMouseDown={closeModal}>
          <div
            style={modal}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18 }}>
                  {selected?.name || "Saved birthday"}
                </div>
                <div style={{ opacity: 0.8 }}>{selected?.birthday || ""}</div>
              </div>

              <button type="button" onClick={closeModal} style={closeBtn}>
                ✕
              </button>
            </div>

            {/* Result Card (matches your Hero vibe) */}
            <div style={resultCard}>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 10 }}>
                Your Chinese Zodiac:
              </div>

              <div style={imgRow}>
                {/* Element icon (if available) */}
                {selected?.element ? (
                  <img
                    src={`/five-elements/${toSlug(selected.element)}-element.png`}
                    alt={`${selected.element} Element`}
                    style={img}
                  />
                ) : null}

                {/* Animal icon (if available) */}
                {selected?.animal ? (
                  <img
                    src={`/chinese-zodiac/${toSlug(selected.animal)}.png`}
                    alt={`${selected.animal} Zodiac`}
                    style={img}
                  />
                ) : null}

                {!selected?.element && !selected?.animal ? (
                  <div style={{ opacity: 0.9 }}>
                    (Missing zodiac data for this saved item)
                  </div>
                ) : null}
              </div>

              <div style={{ fontSize: 22, fontWeight: 900, marginTop: 10 }}>
                {selected?.element && selected?.animal
                  ? `${capitalize(selected.element)} ${capitalize(selected.animal)}`
                  : selected?.animal
                  ? capitalize(selected.animal)
                  : ""}
              </div>
            </div>

            <div style={modalActions}>
              <button
                type="button"
                onClick={() => goToDetails(selected)}
                style={primaryBtn}
              >
                View full details
              </button>

              <button type="button" onClick={closeModal} style={secondaryBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function capitalize(s) {
  const v = String(s || "");
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const row = {
  padding: 12,
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.1)",
  marginBottom: 10,
  background: "#fff",
};

const rowInner = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const iconBtn = {
  width: 42,
  height: 42,
  borderRadius: 6,
  border: "none",
background: "#fff",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  border: "1px solid rgba(0,0,0,0.2)",
};

const iconBtnGhost = {
  width: 42,
  height: 42,
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.2)",
  background: "#fff",
  color: "#111",
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const modal = {
  width: "min(560px, 96vw)",
  background: "#fff",
  borderRadius: 6,
  padding: 18,
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
};

const closeBtn = {
  width: 38,
  height: 38,
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.15)",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 900,
};

const resultCard = {
  marginTop: 14,
  borderRadius: 18,
  padding: 18,
  background: "#800000",
  color: "#fff",
  border: "2px solid rgba(255,255,255,0.8)",
};

const imgRow = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 16,
  marginTop: 6,
};

const img = {
  width: 140,
  height: 140,
  objectFit: "contain",
};

const modalActions = {
  display: "grid",
  gap: 10,
  marginTop: 14,
};

const primaryBtn = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 6,
  border: "none",
  background: "#ffe2b3",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const secondaryBtn = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.2)",
  background: "#fff",
  color: "#111",
  fontWeight: 900,
  cursor: "pointer",
};
